// Smart Sash Admin Panel - Common Models
// Common base types and shared interfaces

/**
 * Standard HTTP Problem Details (RFC 7807)
 * Used for consistent error responses across the API
 */
export interface ProblemDetails {
  type?: string | null;
  title?: string | null;
  status?: number | null;
  detail?: string | null;
  instance?: string | null;
  [key: string]: unknown; // Allows additional properties
}

/**
 * Generic pagination parameters for list queries
 */
export interface PaginationParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Generic pagination metadata for list responses
 */
export interface PaginationMetadata {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/**
 * Generic paginated response wrapper
 */
export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationMetadata;
}

/**
 * v2 API paginated data shape (returned inside ApiResponse envelope)
 */
export interface ApiPaginatedData<T> {
  data: T[];
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

/**
 * Adapt v2 paginated data to existing PaginatedResponse shape.
 * Also handles plain arrays (API endpoints that return T[] without pagination wrapper).
 */
export function toPaginatedResponse<T>(
  apiData: ApiPaginatedData<T> | T[]
): PaginatedResponse<T> {
  if (Array.isArray(apiData)) {
    return {
      data: apiData,
      pagination: {
        currentPage: 1,
        pageSize: apiData.length || 10,
        totalPages: 1,
        totalCount: apiData.length,
        hasNext: false,
        hasPrevious: false,
      },
    };
  }
  return {
    data: apiData.data || apiData.items || [],
    pagination: {
      currentPage: apiData.page,
      pageSize: apiData.pageSize,
      totalPages: apiData.totalPages,
      totalCount: apiData.totalCount,
      hasNext: apiData.page < apiData.totalPages,
      hasPrevious: apiData.page > 1,
    },
  };
}

/**
 * Generic API error response
 */
export interface ApiError {
  message: string;
  code?: string;
  details?: ProblemDetails;
  validationErrors?: Record<string, string[]>;
}

/**
 * Base entity interface with common fields
 */
export interface BaseEntity {
  id: string;
  createdAt: string;
  updatedAt?: string | null;
}

/**
 * Institution-scoped entity base
 */
export interface InstitutionEntity extends BaseEntity {
  institutionId: string;
}
